import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";
import type {
  checksPrintData,
  checksPrintResult,
} from "types/api-types/financial/checks-print/checks-print.types";
import type { checkPrintItem } from "types/api-types/financial/check-print-items/check-print-items.types";
import type { evereeWorkerData } from "types/api-types/payroll/everee-worker-data/everee-worker-data.types";
import CheckPDFGenerator from "./services/CheckPDFGenerator";

/**
 * Check layout type
 */
export type CheckLayoutType = "check-top" | "check-middle" | "check-bottom";

/**
 * Check type - AP or Payroll
 */
export type CheckType = "ap" | "payroll";

/**
 * Check element type - items that can be positioned on the check
 */
export type CheckElementType =
  | "payee_name"
  | "payee_address"
  | "amount_words"
  | "amount_numbers"
  | "date"
  | "check_number"
  | "memo"
  | "signature_line"
  | "bank_info"
  | "company_logo"
  | "stub_details";

/**
 * Check template element with configurable position
 */
export interface CheckTemplateElement {
  id: string;
  type: CheckElementType;
  label: string;
  x: number;           // inches from left edge of check section
  y: number;           // inches from top edge of check section
  width: number;       // max width in inches
  fontSize: number;
  fontWeight: "normal" | "bold";
  alignment: "left" | "center" | "right";
  visible: boolean;
}

/**
 * Check stub element for payment details
 */
export interface StubElement {
  id: string;
  label: string;
  field: "invoice_number" | "invoice_date" | "invoice_amount" | "discount" | "net_amount" | "description";
  x: number;
  y: number;
  width: number;
  fontSize: number;
}

/**
 * Check template with configurable positioning
 */
export interface CheckTemplate {
  id: string;
  name: string;
  companyCode: string;
  layoutType: CheckLayoutType;
  checkHeight: number; // in inches
  stubHeight: number; // in inches
  pageHeight: number; // total page height in inches

  // Global position offsets (for fine-tuning alignment with check stock)
  offsetX: number;  // horizontal offset in inches
  offsetY: number;  // vertical offset in inches

  // Page margins
  marginTop: number;
  marginLeft: number;
  marginRight: number;

  // Check elements with positions
  elements: CheckTemplateElement[];
  stubElements: StubElement[];

  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Payment ready for check printing
 */
export interface PaymentForCheck {
  id: string;
  checkType: CheckType;
  vendorId: string;
  vendorName: string;
  vendorAddress: string;
  vendorCity: string;
  vendorState: string;
  vendorZip: string;
  amount: number;
  checkNumber?: string;
  paymentDate: string;
  memo?: string;
  invoices: PaymentInvoice[];
  status: "pending" | "printed" | "voided";
  plantCode: string;
  bankAccountId?: string;
}

/**
 * Invoice included in payment
 */
export interface PaymentInvoice {
  invoiceNumber: string;
  invoiceDate: string;
  invoiceAmount: number;
  discount: number;
  netAmount: number;
  description?: string;
}

/**
 * Print job configuration
 */
export interface PrintJobConfig {
  templateId: string;
  paymentIds: string[];
  startingCheckNumber: number;
  printOrder: "ascending" | "descending";
  isTestPrint: boolean;
}

/**
 * Payroll worker ready for check printing
 */
export interface PayrollWorkerForCheck {
  workerId: string;
  name: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  hasDirectDeposit: boolean;
  netAmount?: number;
  paymentId?: string | number;
}

/**
 * Rendered check element from API response
 */
export interface RenderedCheckElement {
  id: string;
  elementType: string;
  value: string | null;
  section: "top" | "middle" | "bottom";
  x: number;
  y: number;
  width: number;
  fontSize: number;
  fontWeight: "normal" | "bold";
  alignment: "left" | "center" | "right";
  visible: boolean;
  sortOrder: number;
  label: string;
  rowIndex?: number;
}

/**
 * Rendered check from API response
 */
export interface RenderedCheck {
  checkNumber: number;
  payee: string;
  workerId: string;
  paymentId: string | number;
  netAmount: number;
  elements: RenderedCheckElement[];
}

/**
 * Check Printing Store
 *
 * Manages check templates and printing functionality
 */
class CheckPrintingStore {
  rootStore: RootStore;

  // Data
  templates: CheckTemplate[] = [];
  payments: PaymentForCheck[] = [];
  payrollWorkers: PayrollWorkerForCheck[] = [];
  selectedTemplateId: string | null = null;
  selectedPaymentIds: string[] = [];
  selectedWorkerIds: string[] = [];

  // UI State
  loading = false;
  loadingPayroll = false;
  error: string | null = null;

  // Print configuration
  startingCheckNumber = 1001;
  printOrder: "ascending" | "descending" = "ascending";
  checkTypeFilter: CheckType | "all" = "all";

  // Payroll-specific state
  selectedPayDate: string = "";

  // Last print result for preview/confirmation
  lastPrintResult: checksPrintResult | null = null;
  lastPrintJobId: string | null = null;

  // Template designer state
  editingTemplate: CheckTemplate | null = null;
  selectedElementId: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Computed Properties ====================

  /**
   * Get selected template
   */
  get selectedTemplate(): CheckTemplate | null {
    if (!this.selectedTemplateId) return null;
    return this.templates.find((t) => t.id === this.selectedTemplateId) || null;
  }

  /**
   * Get selected payments
   */
  get selectedPayments(): PaymentForCheck[] {
    return this.payments.filter((p) => this.selectedPaymentIds.includes(p.id));
  }

  /**
   * Get filtered payments based on check type filter
   */
  get filteredPayments(): PaymentForCheck[] {
    if (this.checkTypeFilter === "all") {
      return this.payments;
    }
    return this.payments.filter((p) => p.checkType === this.checkTypeFilter);
  }

  /**
   * Get pending payments (not yet printed)
   */
  get pendingPayments(): PaymentForCheck[] {
    return this.filteredPayments.filter((p) => p.status === "pending");
  }

  /**
   * Get printed payments
   */
  get printedPayments(): PaymentForCheck[] {
    return this.filteredPayments.filter((p) => p.status === "printed");
  }

  /**
   * Get total amount of selected payments
   */
  get selectedTotalAmount(): number {
    return this.selectedPayments.reduce((sum, p) => sum + p.amount, 0);
  }

  /**
   * Get templates for current company
   */
  get companyTemplates(): CheckTemplate[] {
    const plantCode = this.rootStore.appLayoutStore?.selectedCompanyId;
    if (!plantCode) return this.templates;
    return this.templates.filter(
      (t) => t.companyCode === plantCode || t.companyCode === "ALL"
    );
  }

  /**
   * Get default template for current company
   */
  get defaultTemplate(): CheckTemplate | null {
    return this.companyTemplates.find((t) => t.isDefault) || this.companyTemplates[0] || null;
  }

  /**
   * Get selected payroll workers
   */
  get selectedPayrollWorkers(): PayrollWorkerForCheck[] {
    return this.payrollWorkers.filter((w) => this.selectedWorkerIds.includes(w.workerId));
  }

  /**
   * Get payroll workers without direct deposit (check printing candidates)
   */
  get checkPrintableWorkers(): PayrollWorkerForCheck[] {
    return this.payrollWorkers.filter((w) => !w.hasDirectDeposit);
  }

  /**
   * Get total amount of selected payroll workers
   */
  get selectedPayrollTotalAmount(): number {
    return this.selectedPayrollWorkers.reduce((sum, w) => sum + (w.netAmount || 0), 0);
  }

  /**
   * Get the current company code from queryParams or appLayoutStore
   */
  get currentCompanyCode(): string {
    // First check queryParams (set by EvereeCompanySelect)
    const queryCompanyId = this.rootStore.routerStore?.queryParams?.companyId as string;
    if (queryCompanyId) {
      return queryCompanyId;
    }
    // Fall back to appLayoutStore
    return this.rootStore.appLayoutStore?.selectedCompanyId || "";
  }

  // ==================== Actions ====================

  /**
   * Load data
   */
  async loadData(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      await Promise.all([
        this.loadTemplates(),
        this.loadPayments(),
        this.loadNextCheckNumber(),
      ]);

      // Select default template
      if (!this.selectedTemplateId && this.defaultTemplate) {
        this.selectedTemplateId = this.defaultTemplate.id;
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to load check printing data";
    } finally {
      this.loading = false;
    }
  }

  /**
   * Load templates from API (separate templates and elements endpoints)
   */
  private async loadTemplates(): Promise<void> {
    try {
      const companyCode = this.rootStore.appLayoutStore?.selectedCompanyId;
      const checkTemplatesStore = this.rootStore.serviceStores?.["check-templates"];
      const checkTemplateElementsStore = this.rootStore.serviceStores?.["check-template-elements"];

      if (checkTemplatesStore && checkTemplateElementsStore) {
        // 1. Load templates
        const templatesResult = await checkTemplatesStore.find({
          query: companyCode ? { companyCode } : {},
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const apiTemplates: any[] = templatesResult.data || templatesResult;

        // 2. Load elements for all templates
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let apiElements: any[] = [];
        if (apiTemplates.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const templateIds = apiTemplates.map((t: any) => t.id);
          const elementsResult = await checkTemplateElementsStore.find({
            query: { templateId: { $in: templateIds } },
            paginate: false,
          });
          apiElements = elementsResult.data || elementsResult;
        }

        // 3. Map to local format with merged elements
        this.templates = apiTemplates.map((t) =>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.mapApiTemplateToLocal(t, apiElements.filter((el: any) => el.templateId === t.id))
        );
      }
    } catch (err) {
      this.templates = [];
    }
  }

  /**
   * Map API template format to local format
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapApiTemplateToLocal(apiTemplate: any, apiElements: any[]): CheckTemplate {
    return {
      id: apiTemplate.id,
      name: apiTemplate.name,
      companyCode: apiTemplate.companyCode,
      layoutType: apiTemplate.layoutType || "check-middle",
      checkHeight: apiTemplate.checkHeight || 3.5,
      stubHeight: apiTemplate.stubTopHeight || 2.75, // Map stubTopHeight to stubHeight
      pageHeight: apiTemplate.pageHeight || 11,
      offsetX: apiTemplate.offsetX || 0,
      offsetY: apiTemplate.offsetY || 0,
      marginTop: apiTemplate.marginTop || 0,
      marginLeft: apiTemplate.marginLeft || 0,
      marginRight: apiTemplate.marginRight || 0,
      // Map elements from separate API call
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      elements: apiElements.map((el: any) => ({
        id: el.id,
        type: el.elementType,
        label: el.label,
        x: el.x,
        y: el.y,
        width: el.width,
        fontSize: el.fontSize,
        fontWeight: el.fontWeight,
        alignment: el.alignment,
        visible: el.visible,
      })),
      stubElements: [],
      isDefault: apiTemplate.isDefault || false,
      createdAt: apiTemplate.createdAt,
      updatedAt: apiTemplate.updatedAt,
    };
  }

  /**
   * Load AP payments ready for check printing
   */
  private async loadPayments(): Promise<void> {
    try {
      const paymentsStore = this.rootStore.serviceStores?.["payments"];
      const vendorsStore = this.rootStore.serviceStores?.["vendors"];

      if (!paymentsStore) {
        return;
      }

      // Fetch payments in 'scheduled' status without check numbers
      const paymentsResult = await paymentsStore.find({
        query: {
          status: "scheduled",
          paymentMethod: "check",
          $limit: 500,
        },
        paginate: false,
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const apiPayments: any[] = paymentsResult.data || paymentsResult;

      // Filter out payments that already have check numbers
      const eligiblePayments = apiPayments.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (p: any) => !p.checkNumber
      );

      // Get vendor IDs for lookup
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const vendorIds = [...new Set(eligiblePayments.map((p: any) => p.vendorId))];

      // Fetch vendor data if we have vendor IDs
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const vendorMap: Map<string, any> = new Map();
      if (vendorsStore && vendorIds.length > 0) {
        const vendorsResult = await vendorsStore.find({
          query: { id: { $in: vendorIds } },
          paginate: false,
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const vendors: any[] = vendorsResult.data || vendorsResult;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        vendors.forEach((v: any) => vendorMap.set(v.id, v));
      }

      // Map to local format
      runInAction(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.payments = eligiblePayments.map((p: any) => {
          const vendor = vendorMap.get(p.vendorId);

          // Map invoice data if available from the API (populated by backend hook)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const invoices: PaymentInvoice[] = (p.invoices || []).map((inv: any) => ({
            invoiceNumber: inv.invoiceNumber || inv.number || "",
            invoiceDate: inv.invoiceDate || inv.date || "",
            invoiceAmount: inv.invoiceAmount || inv.amount || 0,
            discount: inv.discount || 0,
            netAmount: inv.netAmount || inv.net || (inv.invoiceAmount || inv.amount || 0) - (inv.discount || 0),
            description: inv.description || "",
          }));

          return {
            id: p.id,
            checkType: "ap" as CheckType,
            vendorId: p.vendorId,
            vendorName: vendor?.name || "Unknown Vendor",
            vendorAddress: vendor?.address1 || "",
            vendorCity: vendor?.city || "",
            vendorState: vendor?.state || "",
            vendorZip: vendor?.zip || "",
            amount: p.amount,
            checkNumber: p.checkNumber,
            paymentDate: p.paymentDate,
            memo: p.memo || "",
            invoices: invoices,
            status: "pending" as const,
            plantCode: vendor?.plantId || "",
            bankAccountId: p.bankAccountId,
          };
        });
      });
    } catch (err) {
      runInAction(() => {
        this.payments = [];
      });
    }
  }

  /**
   * Load payroll workers from Everee for check printing
   * If a payDate is selected, the backend will populate netAmount from payment history
   */
  async loadPayrollWorkers(): Promise<void> {
    if (!this.currentCompanyCode) {
      return;
    }

    this.loadingPayroll = true;
    try {
      const evereeWorkerDataStore = this.rootStore.serviceStores?.["everee-worker-data"];

      if (!evereeWorkerDataStore) {
        throw new Error("evereeWorkerDataStore not available");
      }

      // Build query - include payDate if selected to get net amounts
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const query: any = {
        companyCode: this.currentCompanyCode,
      };

      // Include payDate to have the backend hook populate net amounts
      if (this.selectedPayDate) {
        query.payDate = this.selectedPayDate;
      }

      // Fetch workers from Everee
      const workersResult = await evereeWorkerDataStore.find({ query });
      const workers: evereeWorkerData[] = workersResult.data || workersResult;

      runInAction(() => {
        this.payrollWorkers = workers.map((w) => this.mapEvereeWorkerToLocal(w));
      });
    } catch (err) {
      runInAction(() => {
        this.payrollWorkers = [];
        this.error = err instanceof Error ? err.message : "Failed to load payroll workers";
      });
    } finally {
      runInAction(() => {
        this.loadingPayroll = false;
      });
    }
  }


  /**
   * Map Everee worker data to local format
   * Note: netAmount and paymentId may be populated by the backend hook
   * when a payDate is specified in the query
   */
  private mapEvereeWorkerToLocal(worker: evereeWorkerData): PayrollWorkerForCheck {
    const homeAddress = worker.homeAddress?.current;
    const hasBankAccounts = worker.bankAccounts && worker.bankAccounts.length > 0;
    const hasDirectDeposit = worker.preferredPaymentMethod === "DIRECT_DEPOSIT" && hasBankAccounts;

    // The backend hook populates these fields from payment history
    // when a payDate is specified in the query
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const workerWithPayment = worker as any;
    const netAmount = workerWithPayment.netAmount ?? undefined;
    const paymentId = workerWithPayment.paymentId ?? undefined;

    return {
      workerId: worker.workerId,
      name: `${worker.firstName} ${worker.lastName}`.trim(),
      address: homeAddress?.line1 || "",
      city: homeAddress?.city || "",
      state: homeAddress?.state || "",
      postalCode: homeAddress?.postalCode || "",
      hasDirectDeposit,
      netAmount,
      paymentId,
    };
  }

  /**
   * Set the selected pay date for payroll mode
   * Reloads workers to get net amounts for the selected pay date
   */
  setSelectedPayDate(date: string): void {
    this.selectedPayDate = date;
    // Clear worker selections when pay date changes
    this.selectedWorkerIds = [];

    // Reload workers to get net amounts for this pay date
    if (date) {
      this.loadPayrollWorkers();
    }
  }

  /**
   * Toggle payroll worker selection
   */
  toggleWorkerSelection(workerId: string): void {
    const index = this.selectedWorkerIds.indexOf(workerId);
    if (index === -1) {
      this.selectedWorkerIds.push(workerId);
    } else {
      this.selectedWorkerIds.splice(index, 1);
    }
  }

  /**
   * Select all check-printable workers
   */
  selectAllWorkers(): void {
    this.selectedWorkerIds = this.checkPrintableWorkers.map((w) => w.workerId);
  }

  /**
   * Clear worker selection
   */
  clearWorkerSelection(): void {
    this.selectedWorkerIds = [];
  }

  /**
   * Select template
   */
  selectTemplate(templateId: string): void {
    this.selectedTemplateId = templateId;
  }

  /**
   * Toggle payment selection
   */
  togglePaymentSelection(paymentId: string): void {
    const index = this.selectedPaymentIds.indexOf(paymentId);
    if (index === -1) {
      this.selectedPaymentIds.push(paymentId);
    } else {
      this.selectedPaymentIds.splice(index, 1);
    }
  }

  /**
   * Select all pending payments
   */
  selectAllPending(): void {
    this.selectedPaymentIds = this.pendingPayments.map((p) => p.id);
  }

  /**
   * Clear payment selection
   */
  clearSelection(): void {
    this.selectedPaymentIds = [];
  }

  /**
   * Set starting check number
   */
  setStartingCheckNumber(num: number): void {
    this.startingCheckNumber = num;
  }

  /**
   * Set print order
   */
  setPrintOrder(order: "ascending" | "descending"): void {
    this.printOrder = order;
  }

  /**
   * Set check type filter
   */
  setCheckTypeFilter(filter: CheckType | "all"): void {
    this.checkTypeFilter = filter;
    this.clearSelection();
  }

  /**
   * Execute check print via API
   * Returns the print result with rendered checks
   */
  async executeCheckPrint(isTestPrint: boolean): Promise<checksPrintResult | null> {
    if (!this.selectedTemplateId) {
      this.error = "No template selected";
      return null;
    }

    const isPayrollMode = this.checkTypeFilter === "payroll";

    // Validate selections based on mode
    if (isPayrollMode) {
      if (this.selectedWorkerIds.length === 0) {
        this.error = "No workers selected";
        return null;
      }
      if (!this.selectedPayDate) {
        this.error = "No pay date selected";
        return null;
      }
    } else {
      if (this.selectedPaymentIds.length === 0) {
        this.error = "No payments selected";
        return null;
      }
    }

    this.loading = true;
    this.error = null;

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const api = (this.rootStore as any).api;

      if (!api) {
        throw new Error("API not available");
      }

      // Build request payload based on mode
      let payload: checksPrintData;

      if (isPayrollMode) {
        payload = {
          mode: "payroll",
          templateId: this.selectedTemplateId,
          companyCode: this.currentCompanyCode,
          startingCheckNumber: this.startingCheckNumber,
          printOrder: this.printOrder,
          isTestPrint,
          payDate: this.selectedPayDate,
          workerIds: this.selectedWorkerIds,
          filterDirectDeposit: true,
        };
      } else {
        payload = {
          mode: "ap",
          templateId: this.selectedTemplateId,
          paymentIds: this.selectedPaymentIds,
          startingCheckNumber: this.startingCheckNumber,
          printOrder: this.printOrder,
          isTestPrint,
        };
      }

      // Call the checks-print API
      const result: checksPrintResult = await api.service("checks-print").create(payload);

      runInAction(() => {
        this.lastPrintResult = result;
        this.lastPrintJobId = result.printJobId;

        // Update starting check number for next batch
        if (!isTestPrint && result.endingCheckNumber) {
          this.startingCheckNumber = result.endingCheckNumber + 1;
        }

        // Clear selections after successful print (not test print)
        if (!isTestPrint) {
          if (isPayrollMode) {
            this.selectedWorkerIds = [];
          } else {
            this.selectedPaymentIds = [];
          }
        }
      });

      return result;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to print checks";
      });
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Print checks - orchestrates the full print workflow
   */
  async printChecks(isTestPrint = false, openConfirmation = true): Promise<void> {
    // Execute the print via API
    const result = await this.executeCheckPrint(isTestPrint);

    if (!result) {
      return; // Error already set
    }

    // Check for partial failures
    if (result.status === "partial_failure" && result.errors) {
      this.error = `Some checks failed to print: ${result.errors.map((e) => e.error).join(", ")}`;
    }

    // Generate and open PDF for printing/preview
    if (result.checks && result.template) {
      // Open PDF in new tab for printing
      this.previewPDF(isTestPrint);
    }

    // Open confirmation dialog if requested (for actual prints, not test)
    // The dialog will read from lastPrintResult in the store
    if (openConfirmation && !isTestPrint) {
      this.rootStore.routerStore?.openDialog({
        dialogName: "printConfirmation",
        dialogContext: result.printJobId,
      });
    }

    // If test print, also open preview dialog with rendered checks info
    // The dialog will read from lastPrintResult in the store
    if (isTestPrint && result.checks && result.template) {
      this.rootStore.routerStore?.openDialog({
        dialogName: "printPreview",
        dialogContext: "preview",
      });
    }
  }

  /**
   * Get the last print result (for dialogs)
   */
  getLastPrintResult(): checksPrintResult | null {
    return this.lastPrintResult;
  }

  /**
   * Generate PDF from the last print result and open in new tab
   */
  previewPDF(isTestPrint = false): void {
    const result = this.lastPrintResult;

    if (!result || !result.checks || !result.template) {
      this.error = "No print result available for PDF generation";
      return;
    }

    // Map API response to RenderedCheck format
    const checks: RenderedCheck[] = result.checks.map((check) => ({
      checkNumber: check.checkNumber,
      payee: check.payee,
      workerId: check.workerId,
      paymentId: check.paymentId,
      netAmount: check.netAmount,
      elements: check.elements.map((el) => ({
        id: el.id,
        elementType: el.elementType,
        value: el.value,
        section: el.section,
        x: el.x,
        y: el.y,
        width: el.width,
        fontSize: el.fontSize,
        fontWeight: el.fontWeight,
        alignment: el.alignment,
        visible: el.visible,
        sortOrder: el.sortOrder,
        label: el.label,
        rowIndex: el.rowIndex,
      })),
    }));

    const pdfGenerator = new CheckPDFGenerator({
      showTestWatermark: isTestPrint,
    });

    pdfGenerator.generateAndPreview(checks, result.template);
  }

  /**
   * Generate PDF from the last print result and download
   */
  downloadPDF(filename?: string): void {
    const result = this.lastPrintResult;

    if (!result || !result.checks || !result.template) {
      this.error = "No print result available for PDF generation";
      return;
    }

    // Map API response to RenderedCheck format
    const checks: RenderedCheck[] = result.checks.map((check) => ({
      checkNumber: check.checkNumber,
      payee: check.payee,
      workerId: check.workerId,
      paymentId: check.paymentId,
      netAmount: check.netAmount,
      elements: check.elements.map((el) => ({
        id: el.id,
        elementType: el.elementType,
        value: el.value,
        section: el.section,
        x: el.x,
        y: el.y,
        width: el.width,
        fontSize: el.fontSize,
        fontWeight: el.fontWeight,
        alignment: el.alignment,
        visible: el.visible,
        sortOrder: el.sortOrder,
        label: el.label,
        rowIndex: el.rowIndex,
      })),
    }));

    const pdfGenerator = new CheckPDFGenerator();
    const defaultFilename = `checks-${result.printJobId}.pdf`;
    pdfGenerator.generateAndDownload(checks, result.template, filename || defaultFilename);
  }

  /**
   * Void a printed check by its ID
   */
  async voidCheck(checkPrintItemId: string, reason?: string): Promise<void> {
    try {
      const checkPrintItemsStore = this.rootStore.serviceStores?.["check-print-items"];

      if (!checkPrintItemsStore) {
        throw new Error("checkPrintItemsStore not available");
      }

      await checkPrintItemsStore.patch(checkPrintItemId, {
        status: "voided",
        voidReason: reason,
        voidedAt: new Date().toISOString(),
      });

      // Refresh data
      await this.loadData();
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to void check";
    }
  }

  /**
   * Void a printed check by check number
   * Looks up the check print item by check number from the last print job
   */
  async voidCheckByNumber(checkNumber: number, reason?: string): Promise<void> {
    try {
      const checkPrintItemsStore = this.rootStore.serviceStores?.["check-print-items"];

      if (!checkPrintItemsStore) {
        throw new Error("checkPrintItemsStore not available");
      }

      // Find the check print item by check number and last print job
      const printJobId = this.lastPrintJobId;
      if (!printJobId) {
        throw new Error("No print job ID available");
      }

      const result = await checkPrintItemsStore.find({
        query: {
          printJobId,
          checkNumber: String(checkNumber),
          $limit: 1,
        },
      });

      const items = result.data || result;
      if (!items || items.length === 0) {
        throw new Error(`Check #${checkNumber} not found in print job`);
      }

      const checkItem = items[0];

      // Void the check
      await checkPrintItemsStore.patch(checkItem.id, {
        status: "voided",
        voidReason: reason,
        voidedAt: new Date().toISOString(),
      });
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to void check";
      throw err;
    }
  }

  /**
   * Reprint voided checks
   * Creates a new print job for previously voided checks
   */
  async reprintVoidedChecks(
    checkPrintItemIds: string[],
    templateId?: string
  ): Promise<checksPrintResult | null> {
    if (checkPrintItemIds.length === 0) {
      this.error = "No checks selected for reprinting";
      return null;
    }

    this.loading = true;
    this.error = null;

    try {
      const checkPrintItemsStore = this.rootStore.serviceStores?.["check-print-items"];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const api = (this.rootStore as any).api;

      if (!checkPrintItemsStore || !api) {
        throw new Error("Required stores not available");
      }

      // Get the voided check items
      const result = await checkPrintItemsStore.find({
        query: {
          id: { $in: checkPrintItemIds },
          status: "voided",
        },
      });

      const voidedItems = result.data || result;
      if (!voidedItems || voidedItems.length === 0) {
        throw new Error("No voided checks found for reprinting");
      }

      // Determine mode from the first item
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const firstItem = voidedItems[0] as any;
      const isPayroll = firstItem.payeeType === "employee";

      // Build reprint request
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = {
        mode: isPayroll ? "payroll" : "ap",
        templateId: templateId || this.selectedTemplateId,
        companyCode: this.currentCompanyCode,
        startingCheckNumber: this.startingCheckNumber,
        printOrder: this.printOrder,
        isTestPrint: false,
        isReprint: true,
        reprintCheckItemIds: checkPrintItemIds,
      };

      // Call the checks-print API with reprint flag
      const printResult: checksPrintResult = await api.service("checks-print").create(payload);

      runInAction(() => {
        this.lastPrintResult = printResult;
        this.lastPrintJobId = printResult.printJobId;

        // Update starting check number for next batch
        if (printResult.endingCheckNumber) {
          this.startingCheckNumber = printResult.endingCheckNumber + 1;
        }

        // Mark original items as reprinted
        for (const itemId of checkPrintItemIds) {
          checkPrintItemsStore.patch(itemId, {
            status: "reprinted",
          });
        }
      });

      return printResult;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to reprint checks";
      });
      return null;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Get voided checks available for reprinting
   */
  async getVoidedChecksForReprint(): Promise<checkPrintItem[]> {
    try {
      const checkPrintItemsStore = this.rootStore.serviceStores?.["check-print-items"];

      if (!checkPrintItemsStore) {
        throw new Error("checkPrintItemsStore not available");
      }

      const result = await checkPrintItemsStore.find({
        query: {
          status: "voided",
          $sort: { createdAt: -1 },
          $limit: 100,
        },
      });

      return result.data || result;
    } catch (err) {
      return [];
    }
  }

  /**
   * Load the next available check number from the sequence API
   */
  async loadNextCheckNumber(): Promise<void> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const api = (this.rootStore as any).api;
      if (!api) return;

      const companyCode = this.currentCompanyCode;
      if (!companyCode) return;

      const result = await api.service("check-number-sequences/next").find({
        query: { companyCode },
      });

      runInAction(() => {
        if (result?.nextNumber) {
          this.startingCheckNumber = result.nextNumber;
        }
      });
    } catch (err) {
      // Silently fail - will use default check number
    }
  }

  /**
   * Get element by type from template
   */
  private getElement(template: CheckTemplate, type: CheckElementType): CheckTemplateElement | undefined {
    return template.elements.find((e) => e.type === type);
  }

  /**
   * Generate print content HTML
   */
  private generatePrintContent(config: PrintJobConfig): string {
    const template = this.templates.find((t) => t.id === config.templateId);
    if (!template) return "";

    const sortedPayments =
      config.printOrder === "ascending"
        ? this.selectedPayments
        : [...this.selectedPayments].reverse();

    let checkNumber = config.startingCheckNumber;
    const checksHtml = sortedPayments
      .map((payment) => {
        const html = this.renderCheck(template, payment, checkNumber, config.isTestPrint);
        checkNumber++;
        return html;
      })
      .join('<div style="page-break-after: always;"></div>');

    // Calculate margins with offsets
    const marginTop = template.marginTop + template.offsetY;
    const marginLeft = template.marginLeft + template.offsetX;

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Check Print${config.isTestPrint ? " - TEST" : ""}</title>
        <style>
          @page {
            size: 8.5in ${template.pageHeight}in;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
            font-family: 'Courier New', monospace;
          }
          .check-page {
            width: 8.5in;
            height: ${template.pageHeight}in;
            position: relative;
            box-sizing: border-box;
            padding-top: ${marginTop}in;
            padding-left: ${marginLeft}in;
            padding-right: ${template.marginRight}in;
          }
          .check-section {
            position: relative;
            width: 100%;
            height: ${template.checkHeight}in;
            border-bottom: 1px dashed #ccc;
          }
          .stub-section {
            position: relative;
            width: 100%;
            height: ${template.stubHeight}in;
            border-bottom: 1px dashed #ccc;
            padding: 0.25in;
            box-sizing: border-box;
          }
          .element {
            position: absolute;
          }
          .test-watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: 72px;
            color: rgba(255, 0, 0, 0.2);
            font-weight: bold;
            pointer-events: none;
          }
          .amount-box {
            border: 1px solid #000;
            padding: 2px 8px;
            background: #fff;
          }
          .signature-line {
            border-bottom: 1px solid #000;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 9pt;
          }
          th, td {
            padding: 2px 4px;
            text-align: left;
            border-bottom: 1px solid #eee;
          }
          th {
            font-weight: bold;
            border-bottom: 2px solid #000;
          }
          .amount-col {
            text-align: right;
          }
          @media print {
            .check-section {
              border-bottom: none;
            }
            .stub-section {
              border-bottom: none;
            }
          }
        </style>
      </head>
      <body>
        ${checksHtml}
      </body>
      </html>
    `;
  }

  /**
   * Render element with position from template
   */
  private renderElement(
    element: CheckTemplateElement | undefined,
    content: string,
    additionalStyles = ""
  ): string {
    if (!element || !element.visible) return "";

    const style = `
      top: ${element.y}in;
      left: ${element.x}in;
      width: ${element.width}in;
      font-size: ${element.fontSize}pt;
      font-weight: ${element.fontWeight};
      text-align: ${element.alignment};
      ${additionalStyles}
    `;
    return `<div class="element" style="${style}">${content}</div>`;
  }

  /**
   * Render a single check
   */
  private renderCheck(
    template: CheckTemplate,
    payment: PaymentForCheck,
    checkNumber: number,
    isTestPrint: boolean
  ): string {
    const amountWords = this.numberToWords(payment.amount);

    // Get element positions from template
    const dateEl = this.getElement(template, "date");
    const checkNumEl = this.getElement(template, "check_number");
    const payeeNameEl = this.getElement(template, "payee_name");
    const amountNumEl = this.getElement(template, "amount_numbers");
    const amountWordsEl = this.getElement(template, "amount_words");
    const payeeAddrEl = this.getElement(template, "payee_address");
    const memoEl = this.getElement(template, "memo");
    const sigEl = this.getElement(template, "signature_line");

    const checkSection = `
      <div class="check-section">
        ${isTestPrint ? '<div class="test-watermark">TEST PRINT</div>' : ""}

        <!-- Date -->
        ${this.renderElement(dateEl, this.formatDate(payment.paymentDate))}

        <!-- Check Number -->
        ${this.renderElement(checkNumEl, isTestPrint ? "XXXXX" : String(checkNumber))}

        <!-- Pay to the order of -->
        ${payeeNameEl ? `
          <div class="element" style="top: ${payeeNameEl.y - 0.15}in; left: ${payeeNameEl.x}in; font-size: 9pt; color: #666;">
            PAY TO THE ORDER OF
          </div>
        ` : ""}
        ${this.renderElement(payeeNameEl, payment.vendorName)}

        <!-- Amount in numbers -->
        ${amountNumEl ? `
          <div class="element amount-box" style="top: ${amountNumEl.y}in; left: ${amountNumEl.x}in; font-size: ${amountNumEl.fontSize}pt; font-weight: bold;">
            $${this.formatCurrency(payment.amount)}
          </div>
        ` : ""}

        <!-- Amount in words -->
        ${this.renderElement(
          amountWordsEl,
          `${amountWords} DOLLARS <span style="letter-spacing: 3px;">**********************</span>`
        )}

        <!-- Vendor Address -->
        ${this.renderElement(
          payeeAddrEl,
          `${payment.vendorAddress}<br/>${payment.vendorCity}, ${payment.vendorState} ${payment.vendorZip}`
        )}

        <!-- Memo -->
        ${this.renderElement(memoEl, `MEMO: ${payment.memo || "Payment"}`)}

        <!-- Signature Line -->
        ${sigEl ? `
          <div class="element signature-line" style="top: ${sigEl.y}in; left: ${sigEl.x}in; width: ${sigEl.width}in;">
          </div>
          <div class="element" style="top: ${sigEl.y + 0.15}in; left: ${sigEl.x}in; width: ${sigEl.width}in; font-size: 8pt; text-align: right;">
            Authorized Signature
          </div>
        ` : ""}
      </div>
    `;

    const stubSection = `
      <div class="stub-section">
        <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
          <div><strong>${payment.vendorName}</strong></div>
          <div>Check #: ${isTestPrint ? "XXXXX" : checkNumber}</div>
          <div>Date: ${this.formatDate(payment.paymentDate)}</div>
          <div><strong>Amount: $${this.formatCurrency(payment.amount)}</strong></div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Date</th>
              <th>Description</th>
              <th class="amount-col">Amount</th>
              <th class="amount-col">Discount</th>
              <th class="amount-col">Net</th>
            </tr>
          </thead>
          <tbody>
            ${payment.invoices
              .map(
                (inv) => `
              <tr>
                <td>${inv.invoiceNumber}</td>
                <td>${this.formatDate(inv.invoiceDate)}</td>
                <td>${inv.description || ""}</td>
                <td class="amount-col">$${this.formatCurrency(inv.invoiceAmount)}</td>
                <td class="amount-col">$${this.formatCurrency(inv.discount)}</td>
                <td class="amount-col">$${this.formatCurrency(inv.netAmount)}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </div>
    `;

    // Build page based on layout
    if (template.layoutType === "check-top") {
      return `
        <div class="check-page">
          ${checkSection}
          ${stubSection}
          ${stubSection}
        </div>
      `;
    } else {
      // check-middle
      return `
        <div class="check-page">
          ${stubSection}
          ${checkSection}
          ${stubSection}
        </div>
      `;
    }
  }

  /**
   * Convert number to words
   */
  private numberToWords(amount: number): string {
    const ones = [
      "", "ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN", "EIGHT", "NINE",
      "TEN", "ELEVEN", "TWELVE", "THIRTEEN", "FOURTEEN", "FIFTEEN", "SIXTEEN",
      "SEVENTEEN", "EIGHTEEN", "NINETEEN",
    ];
    const tens = [
      "", "", "TWENTY", "THIRTY", "FORTY", "FIFTY", "SIXTY", "SEVENTY", "EIGHTY", "NINETY",
    ];

    const dollars = Math.floor(amount);
    const cents = Math.round((amount - dollars) * 100);

    const convertGroup = (n: number): string => {
      if (n === 0) return "";
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
      return ones[Math.floor(n / 100)] + " HUNDRED" + (n % 100 ? " " + convertGroup(n % 100) : "");
    };

    let result = "";

    if (dollars >= 1000000) {
      result += convertGroup(Math.floor(dollars / 1000000)) + " MILLION ";
      result += convertGroup(Math.floor((dollars % 1000000) / 1000));
      if (Math.floor((dollars % 1000000) / 1000) > 0) result += " THOUSAND ";
      result += convertGroup(dollars % 1000);
    } else if (dollars >= 1000) {
      result += convertGroup(Math.floor(dollars / 1000)) + " THOUSAND ";
      result += convertGroup(dollars % 1000);
    } else {
      result = convertGroup(dollars);
    }

    result = result.trim();
    if (cents > 0) {
      result += ` AND ${cents}/100`;
    } else {
      result += " AND 00/100";
    }

    return result || "ZERO AND 00/100";
  }

  /**
   * Format currency
   */
  private formatCurrency(amount: number): string {
    return amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  /**
   * Format date
   */
  private formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  }

  // ==================== Template Designer ====================

  /**
   * Start editing a template
   */
  startEditingTemplate(templateId: string | null): void {
    if (templateId) {
      const template = this.templates.find((t) => t.id === templateId);
      this.editingTemplate = template ? { ...template } : null;
    } else {
      // Create new template
      this.editingTemplate = this.createDefaultTemplate();
    }
  }

  /**
   * Create default check elements with standard positions
   */
  private createDefaultElements(): CheckTemplateElement[] {
    return [
      {
        id: "date",
        type: "date",
        label: "Date",
        x: 6.0,
        y: 0.5,
        width: 1.5,
        fontSize: 10,
        fontWeight: "normal",
        alignment: "right",
        visible: true,
      },
      {
        id: "check_number",
        type: "check_number",
        label: "Check Number",
        x: 7.0,
        y: 0.25,
        width: 1.0,
        fontSize: 12,
        fontWeight: "bold",
        alignment: "right",
        visible: true,
      },
      {
        id: "payee_name",
        type: "payee_name",
        label: "Pay To (Name)",
        x: 0.75,
        y: 1.2,
        width: 4.0,
        fontSize: 12,
        fontWeight: "bold",
        alignment: "left",
        visible: true,
      },
      {
        id: "amount_numbers",
        type: "amount_numbers",
        label: "Amount ($)",
        x: 6.5,
        y: 1.0,
        width: 1.5,
        fontSize: 14,
        fontWeight: "bold",
        alignment: "right",
        visible: true,
      },
      {
        id: "amount_words",
        type: "amount_words",
        label: "Amount (Words)",
        x: 0.75,
        y: 1.6,
        width: 6.0,
        fontSize: 10,
        fontWeight: "normal",
        alignment: "left",
        visible: true,
      },
      {
        id: "payee_address",
        type: "payee_address",
        label: "Pay To (Address)",
        x: 0.75,
        y: 2.0,
        width: 4.0,
        fontSize: 10,
        fontWeight: "normal",
        alignment: "left",
        visible: true,
      },
      {
        id: "memo",
        type: "memo",
        label: "Memo Line",
        x: 0.75,
        y: 3.0,
        width: 3.0,
        fontSize: 9,
        fontWeight: "normal",
        alignment: "left",
        visible: true,
      },
      {
        id: "signature_line",
        type: "signature_line",
        label: "Signature Line",
        x: 5.0,
        y: 2.9,
        width: 2.5,
        fontSize: 8,
        fontWeight: "normal",
        alignment: "right",
        visible: true,
      },
    ];
  }

  /**
   * Create default template
   */
  private createDefaultTemplate(): CheckTemplate {
    const plantCode = this.rootStore.appLayoutStore?.selectedCompanyId || "ALL";
    return {
      id: `template-${Date.now()}`,
      name: "New Check Template",
      companyCode: plantCode,
      layoutType: "check-top",
      checkHeight: 3.5,
      stubHeight: 3.25,
      pageHeight: 11,
      offsetX: 0,
      offsetY: 0,
      marginTop: 0,
      marginLeft: 0,
      marginRight: 0,
      elements: this.createDefaultElements(),
      stubElements: [],
      isDefault: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  /**
   * Save template
   */
  async saveTemplate(): Promise<void> {
    if (!this.editingTemplate) return;

    this.editingTemplate.updatedAt = new Date().toISOString();

    const checkTemplatesStore = this.rootStore.serviceStores?.["check-templates"];
    if (checkTemplatesStore) {
      const { elements, ...templateData } = this.editingTemplate;
      const isNew = this.editingTemplate.id.startsWith("template-");
      if (isNew) {
        await checkTemplatesStore.create(templateData);
      } else {
        await checkTemplatesStore.patch(this.editingTemplate.id, templateData);
      }
    }

    const existingIndex = this.templates.findIndex(
      (t) => t.id === this.editingTemplate!.id
    );

    if (existingIndex >= 0) {
      this.templates[existingIndex] = { ...this.editingTemplate };
    } else {
      this.templates.push({ ...this.editingTemplate });
    }

    this.editingTemplate = null;
  }

  /**
   * Cancel template editing
   */
  cancelTemplateEdit(): void {
    this.editingTemplate = null;
    this.selectedElementId = null;
  }

  /**
   * Update editing template
   */
  updateEditingTemplate(updates: Partial<CheckTemplate>): void {
    if (!this.editingTemplate) return;
    Object.assign(this.editingTemplate, updates);
  }

  /**
   * Set template as default
   */
  async setAsDefault(templateId: string): Promise<void> {
    this.templates.forEach((t) => {
      t.isDefault = t.id === templateId;
    });
    const checkTemplatesStore = this.rootStore.serviceStores?.["check-templates"];
    if (checkTemplatesStore) {
      await checkTemplatesStore.patch(templateId, { isDefault: true });
    }
  }

  /**
   * Delete template
   */
  async deleteTemplate(templateId: string): Promise<void> {
    const checkTemplatesStore = this.rootStore.serviceStores?.["check-templates"];
    if (checkTemplatesStore) {
      await checkTemplatesStore.remove(templateId);
    }
    const index = this.templates.findIndex((t) => t.id === templateId);
    if (index >= 0) {
      this.templates.splice(index, 1);
    }
  }
}

export default CheckPrintingStore;
